import {
    badgeStyles,
    compactOnDesktopBadgesContainerStyles,
    compactOnDesktopBadgeStyles,
} from "@components/ui/product-badges/styles";
import type { Badge } from "@components/ui/product-badges/utils";
import {
    badgesStyles,
    formatBadges,
    ProductBadgeVariant,
} from "@components/ui/product-badges/utils";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { getValidSaleBadge, isBlackFridayBadge } from "@lib/utils/pdp/saleBadgeUtils";
import type { EccoClubBadge, SaleBadge } from "@lib/wrappers/algolia/types";
import { Tag } from "@ecco/ui/tag";
import { Flex } from "@ecco/ui/flex";
import type { FC } from "react";
import { useMemo } from "react";
import { space } from "@ecco/ui/tokens";

export const BADGES_CONTAINER_TEST_ID = "badges-container";
export const DISCOUNT_BADGE_TEST_ID = "discount";
const ECCO_CLUB_SPLASH_LABEL = "ECCO CLUB";

export interface ProductBadgesProps {
    discount?: string;
    customBadges?: string[];
    saleBadge?: SaleBadge;
    eccoClubBadge?: EccoClubBadge;
    isOnProductTile?: boolean;
    hasCompactBadgesOnDesktop?: boolean;
}

export const ProductBadges: FC<ProductBadgesProps> = ({
    discount,
    customBadges,
    saleBadge,
    eccoClubBadge,
    isOnProductTile = true,
    hasCompactBadgesOnDesktop = false,
}) => {
    const { getMultiple } = useMicrocopy();

    const microcopies = useMemo(
        () =>
            getMultiple(CommonCMS.page, [
                CommonCMS.salesDays.blackFriday,
                CommonCMS.salesDays.cyberMonday,
            ]),
        []
    );

    const earlyAccessSplash: Badge | undefined = saleBadge?.badge
        ? {
              name: saleBadge.badge,
              type: ProductBadgeVariant.ECCO_CLUB_SALE,
              testId: "ecco-club-sale",
          }
        : undefined;
    const validEccoClubBadge = getValidSaleBadge(eccoClubBadge);
    const memberClubSplash: Badge | undefined =
        validEccoClubBadge && !isBlackFridayBadge(validEccoClubBadge)
            ? {
                  name: ECCO_CLUB_SPLASH_LABEL,
                  type: ProductBadgeVariant.ECCO_CLUB_SALE,
                  testId: "ecco-club",
              }
            : undefined;
    const discountBadge: Badge = discount && {
        name: discount,
        type: ProductBadgeVariant.DISCOUNT,
        testId: DISCOUNT_BADGE_TEST_ID,
    };

    const badges = useMemo<Badge[]>(() => {
        const formattedBadges = formatBadges(customBadges, microcopies);
        const allBadges = [
            ...(earlyAccessSplash ? [earlyAccessSplash] : []),
            ...(memberClubSplash ? [memberClubSplash] : []),
            ...formattedBadges,
        ];

        return discountBadge ? [discountBadge, ...allBadges] : allBadges;
    }, [customBadges, discountBadge, earlyAccessSplash, memberClubSplash]);

    const getBadgeDisplay = (index: number) => {
        const defaultDisplay = index > 2 ? "none" : "block";
        if (!hasCompactBadgesOnDesktop) return defaultDisplay;

        const compactDesktopDisplay = index > 1 ? "none" : "block";
        return { base: defaultDisplay, lg: compactDesktopDisplay };
    };

    return (
        <Flex
            alignItems={"center"}
            gap={space["0.2"]}
            flexWrap={"wrap"}
            data-testid={BADGES_CONTAINER_TEST_ID}
            sx={{
                ...(isOnProductTile
                    ? {
                          position: "absolute",
                          top: [3, 4],
                          left: [3, 4, 4, 6],
                          right: 10,
                          zIndex: 1,
                          ...(hasCompactBadgesOnDesktop
                              ? compactOnDesktopBadgesContainerStyles
                              : {}),
                      }
                    : {}),
            }}
        >
            {badges?.map?.((badge, index) => {
                return (
                    <Tag
                        key={`badge-${badge.name}-${index}`}
                        data-testid={`badge-${badge.testId}`}
                        sx={{
                            ...badgeStyles,
                            ...(hasCompactBadgesOnDesktop ? compactOnDesktopBadgeStyles : {}),
                            ...badgesStyles[badge.type],
                        }}
                        display={getBadgeDisplay(index)}
                    >
                        {badge.name}
                    </Tag>
                );
            })}
        </Flex>
    );
};
